import { useLayoutEffect, useRef } from "react";

/**
 * Sizes an element's font so its content exactly fills its own box height — so a
 * page of any narration length keeps the SAME height without scrolling: short
 * scenes render LARGER, longer scenes render slightly SMALLER, every page the
 * same (like a real picture book).
 *
 * It writes a `--fit-scale` number onto the element; the CSS multiplies that into
 * `font-size` (`calc(var(--fit-scale) * clamp(...))`). The scale is clamped to
 * `[min, max]` so text never gets illegibly small or cartoonishly large.
 *
 * Purely presentational: it measures layout (heights), never content — and is
 * independent of playback time, so the moving highlight never triggers a re-fit.
 * Re-fits when `key` (the text) or `max` changes, and when the element's box is
 * resized (orientation / window). Runs in a layout effect so the fitted size is
 * committed before paint (no flash of unscaled text).
 *
 * The scale is set imperatively (a CSS custom property React doesn't manage), so
 * it survives the frequent re-renders that drive highlighting without re-running.
 */
export function useFitText<T extends HTMLElement>(
  key: string,
  { min = 0.6, max = 1.25 }: { min?: number; max?: number } = {},
): React.RefObject<T> {
  const ref = useRef<T>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      let scale = 1;
      el.style.setProperty("--fit-scale", "1");
      // Text height grows ~quadratically with font-size (taller lines AND more
      // of them), so each step multiplies by sqrt(box / natural); a handful of
      // iterations converge even though wrapping shifts as the scale changes.
      for (let i = 0; i < 6; i++) {
        // Natural (unconstrained) content height vs. the CSS-defined box height.
        const prevH = el.style.height;
        const prevMax = el.style.maxHeight;
        el.style.height = "auto";
        el.style.maxHeight = "none";
        const natural = el.scrollHeight;
        el.style.height = prevH;
        el.style.maxHeight = prevMax;
        const box = el.clientHeight;
        if (natural <= 0 || box <= 0) break;

        const next = Math.min(max, Math.max(min, scale * Math.sqrt(box / natural)));
        if (Math.abs(next - scale) < 0.005) {
          scale = next;
          break;
        }
        scale = next;
        el.style.setProperty("--fit-scale", scale.toFixed(4));
      }
      el.style.setProperty("--fit-scale", scale.toFixed(4));
    };

    fit();

    // Observe the PARENT, whose size is layout-driven (page width/height) and so
    // does NOT change when we rescale the text — re-fitting on the element itself
    // would feed back into a ResizeObserver loop.
    const box = el.parentElement ?? el;
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    return () => ro.disconnect();
  }, [key, min, max]);

  return ref;
}
